import React, { useState } from "react";

import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";

import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";

import { DEFAULT_REGION_CODE } from "../../constants/index";
import { ActionTypeItems } from "../../constants/periscope-actions";
import mainStyles from "../../styles";
import DisplayError from "../shared/display-error";
import { listUserAllowedActionsWithGroupInfo } from "api/endpoints/periscope-admin";
import { callOptionsHeaders } from "api/helpers";
import { Validator } from "helpers/validator";

const PermissionSettingsByUser = () => {
  const [oktaUserId, setOktaUserId] = useState("");
  const { validateEmail } = new Validator();
  const [allowedActionsList, setAllowedActionsList] = useState<string[]>([]);
  const [userOktaGroups, setUserOktaGroups] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [openLoadingBackdrop, setOpenLoadingBackdrop] = useState(false);

  const handleSearch = async () => {
    setOpenLoadingBackdrop(true);
    listUserAllowedActionsWithGroupInfo(
      { oktaUserId },
      callOptionsHeaders(DEFAULT_REGION_CODE),
    )
      .then((response) => {
        // get rid of ActionType.UNSPECIFIED that's included in the response allowedMethods
        const index = response.allowedMethods.indexOf(
          ActionType.UNSPECIFIED,
          0,
        );
        if (index > -1) {
          response.allowedMethods.splice(index, 1);
        }
        const allowedActionsReadableList =
          response.allowedMethods.map((action) => {
            const matchedActionItem = ActionTypeItems.find(
              (item) => item.Value == action,
            );
            if (matchedActionItem === undefined) {
              return `N/A - action value ${action}`;
            }
            return `${matchedActionItem.Category} - ${matchedActionItem.DisplayName}`;
          }) || [];
        allowedActionsReadableList.sort((a, b) => (b < a ? 1 : -1));
        setAllowedActionsList(allowedActionsReadableList);

        setUserOktaGroups(
          response.userOktaGroups.sort((a, b) => (b < a ? 1 : -1)),
        );
      })
      .catch((error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setOpenLoadingBackdrop(false);
      });
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault(); // Prevent tab change
      handleSearch();
    }
  };

  const classes = mainStyles();
  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Box
            component="form"
            sx={classes.spaceSearch}
            noValidate
            autoComplete="off"
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center", // Vertically aligns items
                justifyContent: "center", // Horizontally centers content
                gap: 2, // Adds space between TextField and Button
              }}
            >
              <TextField
                required
                label="Okta User Name"
                inputProps={{
                  "data-testid": "user-okta-id",
                }}
                sx={{ minWidth: "300px" }}
                value={oktaUserId}
                onChange={(event) => setOktaUserId(event.target.value)}
                defaultValue={oktaUserId}
                onKeyDown={handleKeyDown} // Listen for the Enter key press
                error={validateEmail(oktaUserId) !== undefined}
              />
              <Button
                variant="contained"
                color="primary"
                data-anchor="user-search-btn"
                data-testid="user-search-btn"
                onClick={handleSearch}
                disabled={validateEmail(oktaUserId) !== undefined}
              >
                Search
              </Button>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="h6">Allowed Actions</Typography>
          <Paper sx={{ padding: 2, textAlign: "left" }}>
            {allowedActionsList.map((item, index) => (
              <Typography key={index}>{item}</Typography>
            ))}
          </Paper>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="h6">Periscope Okta Groups</Typography>
          <Paper sx={{ padding: 2 }}>
            {userOktaGroups.map((item, index) => (
              <Typography key={index}>{item}</Typography>
            ))}
          </Paper>
        </Grid>
      </Grid>
      <DisplayError errorMessage={errorMessage} />
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={openLoadingBackdrop}
        onClick={() => {
          setOpenLoadingBackdrop(false);
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};

export default PermissionSettingsByUser;
